import {
  LEADER_BROADCAST_CHANNEL_NAME,
  LEADER_HEARTBEAT_INTERVAL_MS,
  LEADER_STALE_TIMEOUT_MS,
} from "../constants";

// Cross-tab leader election so only one open tab ever holds the microphone
// and drives the MediaRecorder/upload pipeline for the day's recording.
// Other tabs stay "followers" and only reflect status.
//
// Coordination uses a BroadcastChannel where available, backed by a
// localStorage heartbeat record (`retail-recording-leader`) that also works
// as a fallback signal via the cross-tab `storage` event for browsers/
// contexts without BroadcastChannel support.
const LEADER_STORAGE_KEY = "retail-recording-leader";

type LeaderRecord = {
  tabId: string;
  heartbeatAt: number;
};

type BroadcastMessage =
  | { type: "heartbeat"; tabId: string }
  | { type: "leader-left"; tabId: string }
  | { type: "stop-requested" };

const generateTabId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

const tabId = generateTabId();

const readLeader = (): LeaderRecord | null => {
  const raw = localStorage.getItem(LEADER_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<LeaderRecord>;
    if (
      typeof parsed.tabId !== "string" ||
      typeof parsed.heartbeatAt !== "number"
    ) {
      return null;
    }
    return { tabId: parsed.tabId, heartbeatAt: parsed.heartbeatAt };
  } catch {
    return null;
  }
};

const writeLeader = (record: LeaderRecord): void => {
  localStorage.setItem(LEADER_STORAGE_KEY, JSON.stringify(record));
};

const clearLeaderIfSelf = (): void => {
  if (readLeader()?.tabId === tabId) {
    localStorage.removeItem(LEADER_STORAGE_KEY);
  }
};

const isLeaderStale = (record: LeaderRecord | null): boolean => {
  if (!record) {
    return true;
  }
  return Date.now() - record.heartbeatAt > LEADER_STALE_TIMEOUT_MS;
};

const supportsBroadcastChannel = typeof BroadcastChannel !== "undefined";
const channel = supportsBroadcastChannel
  ? new BroadcastChannel(LEADER_BROADCAST_CHANNEL_NAME)
  : null;

const postMessage = (message: BroadcastMessage): void => {
  channel?.postMessage(message);
};

type LeadershipListener = (isLeader: boolean) => void;
type StopRequestListener = () => void;

const leadershipListeners = new Set<LeadershipListener>();
const stopRequestListeners = new Set<StopRequestListener>();

let currentIsLeader = false;
let heartbeatIntervalId: number | null = null;
let electionIntervalId: number | null = null;
let started = false;

// Kept so tests (and __resetForTests) can precisely remove exactly the
// listeners this module instance registered, rather than relying on
// removeEventListener with a fresh/mismatched function reference.
let messageListener: ((event: MessageEvent) => void) | null = null;
let storageListener: ((event: StorageEvent) => void) | null = null;
let beforeUnloadListener: (() => void) | null = null;

const notifyLeadership = (isLeader: boolean): void => {
  if (currentIsLeader === isLeader) {
    return;
  }
  currentIsLeader = isLeader;
  leadershipListeners.forEach((listener) => listener(isLeader));
};

const becomeLeader = (): void => {
  writeLeader({ tabId, heartbeatAt: Date.now() });
  postMessage({ type: "heartbeat", tabId });
  notifyLeadership(true);

  if (heartbeatIntervalId === null) {
    heartbeatIntervalId = window.setInterval(() => {
      writeLeader({ tabId, heartbeatAt: Date.now() });
      postMessage({ type: "heartbeat", tabId });
    }, LEADER_HEARTBEAT_INTERVAL_MS);
  }
};

const becomeFollower = (): void => {
  if (heartbeatIntervalId !== null) {
    window.clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
  notifyLeadership(false);
};

const attemptElection = (): void => {
  const leader = readLeader();
  if (!leader || leader.tabId === tabId || isLeaderStale(leader)) {
    becomeLeader();
  } else {
    becomeFollower();
  }
};

const releaseLeadership = (): void => {
  if (currentIsLeader) {
    postMessage({ type: "leader-left", tabId });
    clearLeaderIfSelf();
  }
  becomeFollower();
};

const handleMessage = (message: BroadcastMessage): void => {
  if (message.type === "heartbeat" && message.tabId !== tabId) {
    // Extremely rare double-leader race (e.g. two tabs claimed leadership in
    // the same tick): defer to whichever tabId sorts first so both sides
    // converge on the same outcome without further coordination.
    if (currentIsLeader && message.tabId < tabId) {
      becomeFollower();
    }
  }

  if (message.type === "leader-left") {
    attemptElection();
  }

  if (message.type === "stop-requested" && currentIsLeader) {
    stopRequestListeners.forEach((listener) => listener());
  }
};

const ensureStarted = (): void => {
  if (started) {
    return;
  }
  started = true;

  messageListener = (event: MessageEvent) =>
    handleMessage(event.data as BroadcastMessage);
  channel?.addEventListener("message", messageListener);

  // Fallback for browsers without BroadcastChannel (or same-origin contexts
  // where it's unavailable): the cross-tab `storage` event fires in every
  // *other* tab whenever one tab writes to localStorage.
  storageListener = (event: StorageEvent) => {
    if (event.key !== LEADER_STORAGE_KEY) {
      return;
    }
    if (!event.newValue) {
      attemptElection();
    }
  };
  window.addEventListener("storage", storageListener);

  beforeUnloadListener = releaseLeadership;
  window.addEventListener("beforeunload", beforeUnloadListener);

  attemptElection();

  // Safety net in case a leader disappears without cleanly signaling
  // "leader-left" (crashed tab, OS force-quit, etc.) - periodically let
  // followers re-check staleness and claim leadership if needed.
  electionIntervalId = window.setInterval(() => {
    if (!currentIsLeader) {
      attemptElection();
    }
  }, LEADER_HEARTBEAT_INTERVAL_MS);
};

const subscribeToLeadership = (listener: LeadershipListener): (() => void) => {
  ensureStarted();
  leadershipListeners.add(listener);
  listener(currentIsLeader);
  return () => {
    leadershipListeners.delete(listener);
  };
};

const subscribeToStopRequests = (
  listener: StopRequestListener
): (() => void) => {
  stopRequestListeners.add(listener);
  return () => {
    stopRequestListeners.delete(listener);
  };
};

// Followers call this so a Stop click in a follower tab still stops the
// actual recording, which only the leader tab can do.
const requestStop = (): void => {
  postMessage({ type: "stop-requested" });
};

// Explicit, immediate leadership claim triggered by a user gesture (clicking
// "Start recording"). This intentionally bypasses the stale-heartbeat check:
// if a rep deliberately starts a recording from this tab, this tab should be
// the one driving it right away rather than waiting out any timeout.
const claimLeadershipNow = (): void => {
  ensureStarted();
  becomeLeader();
};

// Test-only: fully tear down this module instance's global side effects
// (intervals + window/channel listeners) between test cases, since each
// `jest.isolateModules` reload otherwise leaks listeners onto the shared
// jsdom `window`/`localStorage` that would interfere with later tests.
const __resetForTests = (): void => {
  if (heartbeatIntervalId !== null) {
    window.clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
  if (electionIntervalId !== null) {
    window.clearInterval(electionIntervalId);
    electionIntervalId = null;
  }
  if (messageListener) {
    channel?.removeEventListener("message", messageListener);
    messageListener = null;
  }
  if (storageListener) {
    window.removeEventListener("storage", storageListener);
    storageListener = null;
  }
  if (beforeUnloadListener) {
    window.removeEventListener("beforeunload", beforeUnloadListener);
    beforeUnloadListener = null;
  }
  started = false;
  currentIsLeader = false;
  leadershipListeners.clear();
  stopRequestListeners.clear();
};

export {
  __resetForTests,
  claimLeadershipNow,
  requestStop,
  subscribeToLeadership,
  subscribeToStopRequests,
};
